import { Client } from '@botpress/client'
import { auth, Credentials } from '../auth/index.js'
import { Dependencies, ValidationError, ValidationErrorCode, ValidationSeverity } from '../agent-project/types.js'
import { InterfaceDefinition, ParsedInterface, InterfaceRef, InterfaceValidationResult } from './types.js'
import { EnhancedInterfaceCache } from './enhanced-cache.js'
import { InterfaceParser } from './parser.js'
import { ValidationErrors } from '../agent-project/validation-errors.js'

export interface InterfaceManagerOptions {
  noCache?: boolean
  workspaceId?: string
  credentials?: Credentials
}

export class InterfaceManager {
  private cache: EnhancedInterfaceCache
  private options: InterfaceManagerOptions
  private client?: Client

  constructor(options: InterfaceManagerOptions = {}) {
    this.options = options
    this.cache = new EnhancedInterfaceCache(options.noCache || false)
  }

  private async getClient(): Promise<Client> {
    if (!this.client) {
      const credentials = this.options.credentials || (await auth.getActiveCredentials())

      if (!this.options.workspaceId && !credentials.workspaceId) {
        throw new Error('No workspace ID found in current profile. Please login again with "adk login"')
      }

      this.client = new Client({
        token: credentials.token,
        apiUrl: credentials.apiUrl,
        workspaceId: this.options.workspaceId || credentials.workspaceId,
        headers: {
          'x-multiple-integrations': 'true',
        },
      })
    }
    return this.client
  }

  async loadInterfaces(dependencies: Dependencies): Promise<{
    interfaces: ParsedInterface[]
    errors: ValidationError[]
    warnings: ValidationError[]
  }> {
    const errors: ValidationError[] = []
    const warnings: ValidationError[] = []

    // Parse interfaces from dependencies
    const parseResult = InterfaceParser.parseInterfaces(dependencies)
    const interfaces = parseResult.interfaces
    errors.push(...parseResult.errors)

    // Fetch interface definitions in parallel
    const fetchPromises = interfaces.map(async (intf) => {
      try {
        const definition = await this.fetchInterface(intf.ref)
        intf.definition = definition

        // Validate
        const validation = this.validateInterface(intf)
        intf.validationResult = validation

        if (!validation.valid) {
          validation.errors.forEach((msg) => {
            errors.push(ValidationErrors.warning(msg, 'dependencies.json'))
          })
        }
      } catch (error) {
        if (error instanceof Error && error.message.includes('version')) {
          errors.push(ValidationErrors.unknownInterface(error.message))
        } else {
          errors.push(
            ValidationErrors.invalidDependenciesSyntax(`Unknown interface '${intf.alias}' (${intf.ref.fullName})`)
          )
        }
      }
    })

    await Promise.all(fetchPromises)

    return { interfaces, errors, warnings }
  }

  /**
   * Fetch interface definition from API or cache
   */
  public async fetchInterface(ref: InterfaceRef): Promise<InterfaceDefinition> {
    // Level 1: version resolution cache (name@version -> id + updatedAt)
    const cachedResolution = await this.cache.getResolution(ref.name, ref.version, ref.workspace)
    if (cachedResolution) {
      const cachedDefinition = await this.cache.getDefinition(cachedResolution.interfaceId, cachedResolution.updatedAt)
      if (cachedDefinition) {
        return cachedDefinition
      }
    }

    const client = await this.getClient()
    let interfaceResponse: { interface: InterfaceDefinition } | undefined
    let versionError: Error | null = null

    try {
      interfaceResponse = await client.getInterfaceByName({
        name: ref.name,
        version: ref.version,
      })
    } catch {
      try {
        interfaceResponse = await client.getPublicInterface({
          name: ref.name,
          version: ref.version,
        })
      } catch (publicError) {
        versionError = publicError as Error
      }
    }

    if (!interfaceResponse && versionError) {
      let latestVersion: string | null = null
      try {
        const latestPrivate = await client.getInterfaceByName({
          name: ref.name,
          version: 'latest',
        })
        latestVersion = latestPrivate.interface.version
      } catch {
        try {
          const latestPublic = await client.getPublicInterface({
            name: ref.name,
            version: 'latest',
          })
          latestVersion = latestPublic.interface.version
        } catch {
          const location = ref.workspace ? `workspace "${ref.workspace}"` : 'the official Botpress hub'
          throw new Error(`Interface "${ref.name}" does not exist in ${location}`)
        }
      }
      const location = ref.workspace ? `workspace "${ref.workspace}"` : 'Botpress'
      throw new Error(
        `Interface "${ref.name}" version "${ref.version}" not found in ${location}. Latest available version is "${latestVersion}"`
      )
    }

    const intf = interfaceResponse!.interface

    await this.cache.setResolution(ref.name, ref.version, ref.workspace, intf.id, intf.updatedAt)
    await this.cache.setDefinition(intf.id, intf.updatedAt, intf)
    return intf
  }

  /**
   * Convenience: parse and fetch from a dependency string like "name@1.2.3" or "workspace/name@latest"
   */
  public async getByDependencyString(dep: string): Promise<InterfaceDefinition> {
    // Reuse parser regex logic without duplicating
    const match = dep.match(/^(?:([^/]+)\/)?([^@]+)@(.+)$/)
    if (!match) {
      throw new Error(
        `Invalid interface dependency string: ${dep}. Expected 'name@version' or 'workspace/name@version'`
      )
    }
    const [, workspace, name, version] = match
    return this.fetchInterface({
      workspace: workspace || undefined,
      name: name!,
      version: version!,
      fullName: workspace ? `${workspace}/${name}` : name!,
    })
  }

  private validateInterface(intf: ParsedInterface): InterfaceValidationResult {
    const errors: string[] = []
    const warnings: string[] = []

    if (!intf.definition) {
      errors.push(`Interface definition not found for ${intf.alias}`)
      return { valid: false, errors, warnings }
    }

    return { valid: errors.length === 0, errors, warnings }
  }

  async getCacheStats(): Promise<{ count: number; sizeBytes: number }> {
    const stats = await this.cache.getStats()
    return {
      count: stats.resolutions.count + stats.definitions.count,
      sizeBytes: stats.resolutions.sizeBytes + stats.definitions.sizeBytes,
    }
  }

  async clearCache(): Promise<void> {
    await this.cache.clear()
  }
}
